import * as React from 'react'

/** Loads Iubenda widget overrides; mount only when the Iubenda script is rendered. */
export function IubendaStyles() {
  React.useEffect(() => {
    void import('./iubenda-styles')
  }, [])

  return null
}

type Variant = 'locationNotSet' | 'strictMode' | 'laxMode' | 'notApplicable'

interface IubendaScriptProps {
  type?: 'text/plain'
  className?: string
  'data-cf-defer'?: true
  'data-iub-purposes'?: string
}

interface IubendaUsPrivacyPreferences {
  adv?: boolean
  s?: boolean
  sh?: boolean
}

interface IubendaPreference {
  uspr?: IubendaUsPrivacyPreferences
}

interface IubendaApi {
  activateSnippets?: () => void
  getPreferences?: () => IubendaPreference | null
  isPreferenceExpressed?: () => boolean
  openPreferences?: () => void
}

interface IubendaBridge {
  activateSnippets?: (preference?: IubendaPreference | null) => void
  getApi?: () => IubendaApi | undefined
  openPreferences?: () => boolean
  openPreferencesWhenReady?: () => boolean
  ready?: boolean
}

interface IubendaWindowGlobals {
  CF_IUBENDA?: IubendaBridge
  _iub?: {
    cs?: {
      api?: IubendaApi
    }
    csConfiguration?: {
      callback?: Record<string, (...args: unknown[]) => void>
    }
  }
}

interface CreateIubendaBridgeScriptOptions {
  autoOpenPreferences?: boolean
}

interface UseIubendaOptions {
  country?: string
  enabled?: boolean
  locationSet?: boolean
  region?: string
}

export interface IubendaConsentEventDetail {
  apiAvailable: boolean
  preference: IubendaPreference | null
  preferenceExpressed: boolean
  trackingAllowed: boolean
  type: string
}

export interface IubendaConsentState extends IubendaConsentEventDetail {
  revision: number
}

export const iubendaConsentEventName = 'cf:iubenda-consent'

export const IUBENDA_FEATURE_FLAG = 'iubenda-us-privacy-enabled'

export const iubendaSrc =
  'https://embeds.iubenda.com/widgets/86965d08-6db6-11ee-8bfc-5ad8d8c564c0.js'

export const createIubendaBridgeScript = ({
  autoOpenPreferences = false,
}: CreateIubendaBridgeScriptOptions = {}) => `
(function () {
window._iub = window._iub || {};
window._iub.csConfiguration = window._iub.csConfiguration || {};
window._iub.csConfiguration.callback = window._iub.csConfiguration.callback || {};

var bridge = window.CF_IUBENDA = window.CF_IUBENDA || {};
var consentEventName = '${iubendaConsentEventName}';
var autoOpenPreferences = ${autoOpenPreferences ? 'true' : 'false'};
var cfPreferencesOpened = false;

function cfGetApi() {
    return window._iub && window._iub.cs && window._iub.cs.api;
}

function cfGetPreference(preference) {
    if (preference) {
        return preference;
    }

    var api = cfGetApi();
    if (api && typeof api.getPreferences === 'function') {
        var prefs = api.getPreferences();
        if (prefs) {
            return prefs;
        }
    }

    return null;
}

function cfIsPreferenceExpressed() {
    var api = cfGetApi();
    return !!(api && typeof api.isPreferenceExpressed === 'function' && api.isPreferenceExpressed());
}

function cfDeferTrackingAllowed(preference) {
    if (!cfIsPreferenceExpressed()) {
        return false;
    }

    var normalizedPreference = cfGetPreference(preference);
    var uspr = normalizedPreference && normalizedPreference.uspr;

    if (uspr) {
        return !!(uspr.s || uspr.sh || uspr.adv);
    }

    return false;
}

function cfDispatchIubendaEvent(type, preference) {
    if (typeof window.CustomEvent !== 'function') {
        return;
    }

    var normalizedPreference = cfGetPreference(preference);
    var api = cfGetApi();

    window.dispatchEvent(new CustomEvent(consentEventName, {
        detail: {
            apiAvailable: !!api,
            preference: normalizedPreference,
            preferenceExpressed: cfIsPreferenceExpressed(),
            trackingAllowed: cfDeferTrackingAllowed(normalizedPreference),
            type: type
        }
    }));
}

function cfActivateDeferredScripts() {
    document.querySelectorAll('script[data-cf-defer]:not([data-cf-defer-active])').forEach(function (el) {
        el.setAttribute('data-cf-defer-active', '1');
        var s = document.createElement('script');
        var src = el.getAttribute('data-cf-src');
        if (src) {
            s.src = src;
        } else {
            s.text = el.textContent;
        }
        if (el.hasAttribute('async')) {
            s.async = true;
        }
        if (el.hasAttribute('defer')) {
            s.defer = true;
        }
        el.parentNode.insertBefore(s, el);
    });
}

function cfActivateSnippets(preference) {
    var api = cfGetApi();
    if (api && typeof api.activateSnippets === 'function') {
        api.activateSnippets();
    }

    if (cfDeferTrackingAllowed(preference)) {
        cfActivateDeferredScripts();
    }

    cfDispatchIubendaEvent('activation-done', preference);
}

function cfOpenPreferences() {
    var api = cfGetApi();
    if (!api || typeof api.openPreferences !== 'function') {
        return false;
    }

    cfPreferencesOpened = true;
    api.openPreferences();
    cfDispatchIubendaEvent('preferences-opened');
    return true;
}

function cfCanAutoOpenPreferences() {
    var api = cfGetApi();
    if (!autoOpenPreferences || cfPreferencesOpened) {
        return false;
    }

    if (!api) {
        return true;
    }

    if (typeof api.isPreferenceExpressed === 'function' && api.isPreferenceExpressed()) {
        return false;
    }

    return true;
}

function cfOpenPreferencesWhenReady(attemptsLeft, requireUnexpressedPreference) {
    if (requireUnexpressedPreference && !cfCanAutoOpenPreferences()) {
        return;
    }

    if (cfOpenPreferences()) {
        return;
    }

    if (attemptsLeft > 0) {
        window.setTimeout(function () {
            cfOpenPreferencesWhenReady(attemptsLeft - 1, requireUnexpressedPreference);
        }, 250);
    }
}

function cfAutoOpenPreferencesWhenReady() {
    if (!autoOpenPreferences || cfPreferencesOpened) {
        return;
    }

    window.setTimeout(function () {
        cfOpenPreferencesWhenReady(40, true);
    }, 0);
}

bridge.getApi = cfGetApi;
bridge.openPreferences = function () {
    return cfOpenPreferences();
};
bridge.openPreferencesWhenReady = function () {
    cfOpenPreferencesWhenReady(40, false);
    return true;
};
bridge.activateSnippets = cfActivateSnippets;
bridge.ready = true;

(function (existingOnReady) {
    window._iub.csConfiguration.callback.onReady = function (preference) {
        if (typeof existingOnReady === 'function') {
            existingOnReady.apply(this, arguments);
        }
        cfActivateSnippets(preference);
        cfDispatchIubendaEvent('ready', preference);
        cfAutoOpenPreferencesWhenReady();
    };
}(window._iub.csConfiguration.callback.onReady));

(function (existingCallback) {
    window._iub.csConfiguration.callback.onPreferenceExpressed = function (preference) {
        if (typeof existingCallback === 'function') {
            existingCallback.apply(this, arguments);
        }
        cfActivateSnippets(preference);
        cfDispatchIubendaEvent('preference-expressed', preference);
    };
}(window._iub.csConfiguration.callback.onPreferenceExpressed));

(function (existingCallback) {
    window._iub.csConfiguration.callback.onPreferenceExpressedOrNotNeeded = function (preference) {
        if (typeof existingCallback === 'function') {
            existingCallback.apply(this, arguments);
        }
        cfActivateSnippets(preference);
        cfDispatchIubendaEvent('preference-expressed-or-not-needed', preference);
    };
}(window._iub.csConfiguration.callback.onPreferenceExpressedOrNotNeeded));

(function (existingCallback) {
    window._iub.csConfiguration.callback.onActivationDone = function (preference) {
        if (typeof existingCallback === 'function') {
            existingCallback.apply(this, arguments);
        }
        cfDispatchIubendaEvent('activation-done', preference);
    };
}(window._iub.csConfiguration.callback.onActivationDone));

document.addEventListener('DOMContentLoaded', function () {
    cfActivateSnippets();
});

cfDispatchIubendaEvent('bridge-ready');
}());
`

export const californiaConsentScript = createIubendaBridgeScript({ autoOpenPreferences: true })

const initialIubendaConsentState: IubendaConsentState = {
  apiAvailable: false,
  preference: null,
  preferenceExpressed: false,
  revision: 0,
  trackingAllowed: false,
  type: 'initial',
}

const getIubendaWindow = () => window as Window & typeof globalThis & IubendaWindowGlobals

export function getIubendaApi() {
  if (typeof window === 'undefined') {
    return undefined
  }

  return getIubendaWindow()._iub?.cs?.api
}

const getIubendaBridge = () => {
  if (typeof window === 'undefined') {
    return undefined
  }

  return getIubendaWindow().CF_IUBENDA
}

export const isIubendaBridgeReady = () => {
  return Boolean(getIubendaBridge()?.ready)
}

const getIubendaPreference = (preference?: IubendaPreference | null) => {
  if (preference) {
    return preference
  }

  return getIubendaApi()?.getPreferences?.() ?? null
}

const isIubendaPreferenceExpressed = () => {
  return getIubendaApi()?.isPreferenceExpressed?.() ?? false
}

export const isIubendaTrackingAllowed = (preference?: IubendaPreference | null) => {
  if (!isIubendaPreferenceExpressed()) {
    return false
  }

  const uspr = getIubendaPreference(preference)?.uspr

  return Boolean(uspr?.s || uspr?.sh || uspr?.adv)
}

const readIubendaConsentState = (
  detail?: Partial<IubendaConsentEventDetail>,
  revision = 0,
): IubendaConsentState => {
  if (typeof window === 'undefined') {
    return initialIubendaConsentState
  }

  const preference = detail?.preference ?? getIubendaPreference()

  return {
    apiAvailable: detail?.apiAvailable ?? Boolean(getIubendaApi()),
    preference,
    preferenceExpressed: detail?.preferenceExpressed ?? isIubendaPreferenceExpressed(),
    revision,
    trackingAllowed: detail?.trackingAllowed ?? isIubendaTrackingAllowed(preference),
    type: detail?.type ?? 'snapshot',
  }
}

export function useIubendaConsent() {
  const [state, setState] = React.useState<IubendaConsentState>(() => readIubendaConsentState())

  React.useEffect(() => {
    const handleConsentEvent = (event: Event) => {
      const detail = (event as CustomEvent<Partial<IubendaConsentEventDetail>>).detail

      setState(currentState => readIubendaConsentState(detail, currentState.revision + 1))
    }

    window.addEventListener(iubendaConsentEventName, handleConsentEvent)
    setState(currentState => readIubendaConsentState(undefined, currentState.revision))

    return () => {
      window.removeEventListener(iubendaConsentEventName, handleConsentEvent)
    }
  }, [])

  const openPreferences = React.useCallback(() => {
    if (getIubendaBridge()?.openPreferencesWhenReady?.()) {
      return
    }

    if (getIubendaBridge()?.openPreferences?.()) {
      return
    }

    getIubendaApi()?.openPreferences?.()
  }, [])

  const activateSnippets = React.useCallback((preference?: IubendaPreference | null) => {
    const bridge = getIubendaBridge()
    if (bridge?.activateSnippets) {
      bridge.activateSnippets(preference)
      return
    }

    getIubendaApi()?.activateSnippets?.()
  }, [])

  return React.useMemo(
    () => ({
      ...state,
      activateSnippets,
      openPreferences,
    }),
    [activateSnippets, openPreferences, state],
  )
}

export function useIubendaSnippetActivation(enabled = true) {
  React.useEffect(() => {
    if (!enabled) {
      return
    }

    const bridge = getIubendaBridge()
    if (bridge?.activateSnippets) {
      bridge.activateSnippets()
      return
    }

    getIubendaApi()?.activateSnippets?.()
  }, [enabled])
}

export function useIubenda({
  country,
  enabled = false,
  locationSet = false,
  region,
}: UseIubendaOptions = {}) {
  if (!enabled) {
    return {
      scriptProps: {},
      variant: 'notApplicable' as const,
    }
  }

  const normalizedCountry = country?.trim().toUpperCase()
  const normalizedRegion = region?.trim().toLowerCase()
  const isUncertainCountry =
    !normalizedCountry || normalizedCountry === 'XX' || normalizedCountry === 'T1'
  const isCountryConsideredUs = isUncertainCountry || normalizedCountry === 'US'
  const isCaliforniaRegion = normalizedRegion === 'california' || normalizedRegion === 'ca'
  const isUnknownRegion =
    !normalizedRegion ||
    normalizedRegion === 'unknown' ||
    normalizedRegion === 'xx' ||
    normalizedRegion === 't1'
  let variant: Variant = 'notApplicable'
  let scriptProps: IubendaScriptProps = {}

  if (!locationSet) {
    variant = 'locationNotSet'
  }

  if (locationSet && isCountryConsideredUs) {
    if (isUncertainCountry || isUnknownRegion || isCaliforniaRegion) {
      variant = 'strictMode'
      scriptProps = {
        type: 'text/plain',
        'data-cf-defer': true,
      }
    } else {
      variant = 'laxMode'
      scriptProps = {
        type: 'text/plain',
        className: '_iub_cs_activate',
        'data-iub-purposes': 's,sh,adv',
      }
    }
  }

  return {
    scriptProps,
    variant,
  }
}
